import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  ArrowLeft,
  BarChart3,
  Building2,
  CheckCircle,
  Eye,
  Loader2,
  MousePointerClick,
  Phone,
  Radar,
  RefreshCw,
  Route,
  Target,
  Trash2,
  Unplug,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  Badge,
  Button,
  Card,
  Input,
  PageHeader,
  StatCard,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui';
import {
  businessApi,
  competitorApi,
  yandexApi,
  type Business,
  type Competitor,
  type YandexCompetitorBusinessStatsData,
  type YandexCompetitorPositionsData,
  type MetrikaPrefillData,
  type YandexOAuthAccountType,
  type YandexMetrikaCounter,
  type YandexMetrikaGoal,
} from '../lib/api';

function formatMetric(value?: number): string {
  if (typeof value !== 'number') {
    return '—';
  }

  return value.toLocaleString('ru-RU');
}

function formatDecimal(value?: number): string {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '—';
  }

  return value.toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getApiError(error: unknown, fallback: string): string {
  const payload = error as { response?: { data?: { error?: string } } };
  return payload.response?.data?.error || fallback;
}

function formatDateInput(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getDefaultDateRange(): { dateFrom: string; dateTo: string } {
  const today = new Date();
  return {
    dateFrom: formatDateInput(new Date(today.getFullYear(), today.getMonth(), 1)),
    dateTo: formatDateInput(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
  };
}

interface AdsAnalyticsSnapshot {
  counterId: string;
  goalId: string | null;
  goalName: string | null;
  dateFrom: string;
  dateTo: string;
  impressions: number;
  clicks: number;
  ctr: number;
  spend: number;
  conversions: number;
  costPerConversion: number;
  directTraffic: number;
}

export function ClientAnalytics() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [business, setBusiness] = useState<Business | null>(null);
  const [yandexConnected, setYandexConnected] = useState(false);
  const [adsYandexConnected, setAdsYandexConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [adsConnecting, setAdsConnecting] = useState(false);
  const [adsDisconnecting, setAdsDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dateFrom, setDateFrom] = useState(() => getDefaultDateRange().dateFrom);
  const [dateTo, setDateTo] = useState(() => getDefaultDateRange().dateTo);
  const [counterId, setCounterId] = useState('');
  const [counters, setCounters] = useState<YandexMetrikaCounter[]>([]);
  const [loadingCounters, setLoadingCounters] = useState(false);
  const [countersError, setCountersError] = useState<string | null>(null);

  const [businessAnalytics, setBusinessAnalytics] = useState<MetrikaPrefillData | null>(null);
  const [businessAnalyticsError, setBusinessAnalyticsError] = useState<string | null>(null);
  const [loadingBusinessAnalytics, setLoadingBusinessAnalytics] = useState(false);
  const [competitorQuery, setCompetitorQuery] = useState('');
  const [competitorData, setCompetitorData] = useState<YandexCompetitorPositionsData | null>(null);
  const [competitorError, setCompetitorError] = useState<string | null>(null);
  const [loadingCompetitors, setLoadingCompetitors] = useState(false);
  const [competitorBusinessStats, setCompetitorBusinessStats] = useState<YandexCompetitorBusinessStatsData | null>(null);
  const [competitorBusinessStatsError, setCompetitorBusinessStatsError] = useState<string | null>(null);
  const [loadingCompetitorBusinessStats, setLoadingCompetitorBusinessStats] = useState(false);
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [competitorListLoading, setCompetitorListLoading] = useState(false);
  const [competitorListError, setCompetitorListError] = useState<string | null>(null);
  const [competitorSubmitting, setCompetitorSubmitting] = useState(false);
  const [newCompetitorName, setNewCompetitorName] = useState('');
  const [newCompetitorUrl, setNewCompetitorUrl] = useState('');
  const [adsCounterId, setAdsCounterId] = useState('');
  const [adsGoalId, setAdsGoalId] = useState('');
  const [adsGoals, setAdsGoals] = useState<YandexMetrikaGoal[]>([]);
  const [loadingAdsGoals, setLoadingAdsGoals] = useState(false);
  const [adsGoalsError, setAdsGoalsError] = useState<string | null>(null);
  const [adsAnalytics, setAdsAnalytics] = useState<AdsAnalyticsSnapshot | null>(null);
  const [adsAnalyticsError, setAdsAnalyticsError] = useState<string | null>(null);
  const [loadingAdsAnalytics, setLoadingAdsAnalytics] = useState(false);

  const getAdsCounterStorageKey = (businessId: string) => `reports:adsCounter:${businessId}`;
  const getAdsGoalStorageKey = (businessId: string) => `reports:adsGoal:${businessId}`;

  const loadCounters = async (businessId: string) => {
    try {
      setLoadingCounters(true);
      setCountersError(null);
      const response = await yandexApi.getMetrikaCounters(businessId);
      setCounters(response.data.data);
    } catch (e) {
      console.error(e);
      setCounters([]);
      setCountersError(getApiError(e, 'Не удалось загрузить список счётчиков Яндекс.Метрики'));
    } finally {
      setLoadingCounters(false);
    }
  };

  const loadBusinessAnalytics = async (options?: {
    counterId?: string;
    saveCounter?: boolean;
    silent?: boolean;
  }) => {
    if (!id) return;

    const selectedCounterId = (options?.counterId ?? counterId).trim();
    if (!selectedCounterId) {
      setBusinessAnalyticsError('Укажите ID счётчика Яндекс.Метрики');
      return;
    }

    if (!dateFrom || !dateTo) {
      setBusinessAnalyticsError('Укажите диапазон дат (от и до)');
      return;
    }

    if (dateFrom > dateTo) {
      setBusinessAnalyticsError('Дата начала не может быть позже даты окончания');
      return;
    }

    try {
      setLoadingBusinessAnalytics(true);
      setBusinessAnalyticsError(null);

      const response = await yandexApi.getBusinessAnalytics(id, {
        dateFrom,
        dateTo,
        counterId: selectedCounterId,
        saveCounter: options?.saveCounter ?? true,
      });

      const { metrics, savedCounterId } = response.data.data;
      setBusinessAnalytics(metrics);

      if (savedCounterId) {
        setCounterId(savedCounterId);
        setBusiness((prev) => (prev ? { ...prev, metrikaCounterId: savedCounterId } : prev));
      }

      if (!options?.silent) {
        toast.success('Аналитика с Яндекс Бизнес обновлена');
      }
    } catch (e) {
      console.error(e);
      const message = getApiError(e, 'Не удалось подтянуть аналитику из Яндекс Бизнес');
      setBusinessAnalyticsError(message);
      if (!options?.silent) {
        toast.error(message);
      }
    } finally {
      setLoadingBusinessAnalytics(false);
    }
  };

  const loadCompetitorPositions = async (options?: { query?: string; silent?: boolean }) => {
    if (!id) return;

    const selectedQuery = (options?.query ?? competitorQuery).trim();
    if (!selectedQuery) {
      setCompetitorError('Введите поисковый запрос для анализа конкурентов');
      return;
    }

    try {
      setLoadingCompetitors(true);
      setCompetitorError(null);

      const response = await yandexApi.getCompetitorPositions(id, {
        query: selectedQuery,
        limit: 10,
      });

      setCompetitorData(response.data.data);
      if (business && business.trackedSearchQuery !== selectedQuery) {
        businessApi
          .update(id, { trackedSearchQuery: selectedQuery })
          .then(() => setBusiness((prev) => (prev ? { ...prev, trackedSearchQuery: selectedQuery } : prev)))
          .catch((e) => console.error('Failed to persist trackedSearchQuery', e));
      }
      if (!options?.silent) {
        toast.success('Позиции конкурентов обновлены');
      }
    } catch (e) {
      console.error(e);
      const message = getApiError(
        e,
        'Не удалось получить позиции конкурентов из выдачи Яндекс.Карт',
      );
      setCompetitorError(message);
      if (!options?.silent) {
        toast.error(message);
      }
    } finally {
      setLoadingCompetitors(false);
    }
  };

  const loadCompetitorBusinessStats = async (options?: { silent?: boolean }) => {
    if (!id) return;

    try {
      setLoadingCompetitorBusinessStats(true);
      setCompetitorBusinessStatsError(null);

      const response = await yandexApi.getCompetitorsBusinessStats(id);
      setCompetitorBusinessStats(response.data.data);

      if (!options?.silent) {
        toast.success('Статистика конкурентов за 7 дней обновлена');
      }
    } catch (e) {
      console.error(e);
      const message = getApiError(
        e,
        'Не удалось загрузить статистику конкурентов из Яндекс Бизнес',
      );
      setCompetitorBusinessStats(null);
      setCompetitorBusinessStatsError(message);
      if (!options?.silent) {
        toast.error(message);
      }
    } finally {
      setLoadingCompetitorBusinessStats(false);
    }
  };

  const loadCompetitorList = async () => {
    if (!id) return;

    try {
      setCompetitorListLoading(true);
      setCompetitorListError(null);

      const response = await competitorApi.getAll(id);
      setCompetitors(response.data.data);
    } catch (e) {
      console.error(e);
      setCompetitors([]);
      setCompetitorListError(getApiError(e, 'Не удалось загрузить список конкурентов'));
    } finally {
      setCompetitorListLoading(false);
    }
  };

  const handleAddCompetitor = async () => {
    if (!id) return;

    const name = newCompetitorName.trim();
    const yandexMapsUrl = newCompetitorUrl.trim();
    if (!name || !yandexMapsUrl) {
      setCompetitorListError('Укажите название конкурента и ссылку на Яндекс.Карты');
      return;
    }

    try {
      setCompetitorSubmitting(true);
      setCompetitorListError(null);

      await competitorApi.create({ businessId: id, name, yandexMapsUrl });
      setNewCompetitorName('');
      setNewCompetitorUrl('');

      await loadCompetitorList();
      await loadCompetitorPositions({ query: competitorQuery, silent: true });

      toast.success('Конкурент добавлен');
    } catch (e) {
      console.error(e);
      const message = getApiError(e, 'Не удалось добавить конкурента');
      setCompetitorListError(message);
      toast.error(message);
    } finally {
      setCompetitorSubmitting(false);
    }
  };

  const handleRemoveCompetitor = async (competitorId: string) => {
    try {
      setCompetitorListError(null);

      await competitorApi.remove(competitorId);

      await loadCompetitorList();
      await loadCompetitorPositions({ query: competitorQuery, silent: true });

      toast.success('Конкурент удалён');
    } catch (e) {
      console.error(e);
      const message = getApiError(e, 'Не удалось удалить конкурента');
      setCompetitorListError(message);
      toast.error(message);
    }
  };

  const loadAdsGoals = async (
    businessId: string,
    selectedCounterId: string,
    options?: { silent?: boolean; accountType?: YandexOAuthAccountType },
  ) => {
    const normalizedCounterId = selectedCounterId.trim();
    if (!normalizedCounterId) {
      setAdsGoals([]);
      setAdsGoalsError(null);
      return;
    }

    try {
      setLoadingAdsGoals(true);
      setAdsGoalsError(null);
      const response = await yandexApi.getMetrikaGoals(
        businessId,
        normalizedCounterId,
        options?.accountType ?? 'PRIMARY',
      );
      setAdsGoals(response.data.data);
    } catch (e) {
      console.error(e);
      const message = getApiError(e, 'Не удалось загрузить цели счётчика Метрики');
      setAdsGoals([]);
      setAdsGoalsError(message);
      if (!options?.silent) {
        toast.error(message);
      }
    } finally {
      setLoadingAdsGoals(false);
    }
  };

  const loadAdsAnalytics = async (options?: {
    counterId?: string;
    goalId?: string;
    silent?: boolean;
    accountType?: YandexOAuthAccountType;
  }) => {
    if (!id) return;

    const selectedCounterId = (options?.counterId ?? adsCounterId ?? counterId).trim();
    const selectedGoalId = (options?.goalId ?? adsGoalId).trim();
    if (!selectedCounterId) {
      setAdsAnalyticsError('Укажите ID счётчика Метрики для рекламной аналитики');
      return;
    }

    if (!dateFrom || !dateTo) {
      setAdsAnalyticsError('Укажите диапазон дат (от и до)');
      return;
    }

    if (dateFrom > dateTo) {
      setAdsAnalyticsError('Дата начала не может быть позже даты окончания');
      return;
    }

    try {
      setLoadingAdsAnalytics(true);
      setAdsAnalyticsError(null);

      const response = await yandexApi.getBusinessAnalytics(id, {
        dateFrom,
        dateTo,
        counterId: selectedCounterId,
        goalId: selectedGoalId || undefined,
        saveCounter: false,
        accountType: options?.accountType ?? 'PRIMARY',
      });

      const metrics = response.data.data.metrics;
      const impressions = metrics.views ?? 0;
      const clicks = metrics.websiteClicks ?? 0;
      const ctr = impressions > 0 ? Number(((clicks / impressions) * 100).toFixed(2)) : 0;
      const spend = metrics.adSpend ?? 0;
      const conversions = metrics.adConversions ?? 0;
      const costPerConversion = metrics.adCostPerConversion ?? (spend > 0 && conversions > 0
        ? Number((spend / conversions).toFixed(2))
        : 0);
      const directTraffic = metrics.trafficDirect ?? 0;

      setAdsAnalytics({
        counterId: selectedCounterId,
        goalId: selectedGoalId || metrics.trackedGoalId || null,
        goalName: metrics.trackedGoalName || null,
        dateFrom,
        dateTo,
        impressions,
        clicks,
        ctr,
        spend,
        conversions,
        costPerConversion,
        directTraffic,
      });
      setAdsCounterId(selectedCounterId);
      setAdsGoalId(selectedGoalId);
      window.localStorage.setItem(getAdsCounterStorageKey(id), selectedCounterId);
      window.localStorage.setItem(getAdsGoalStorageKey(id), selectedGoalId);

      if (!options?.silent) {
        toast.success('Рекламная аналитика из Метрики обновлена');
      }
    } catch (e) {
      console.error(e);
      const message = getApiError(e, 'Не удалось получить рекламные данные из Метрики');
      setAdsAnalytics(null);
      setAdsAnalyticsError(message);
      if (!options?.silent) {
        toast.error(message);
      }
    } finally {
      setLoadingAdsAnalytics(false);
    }
  };

  const loadData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const [businessRes, statusRes] = await Promise.all([
        businessApi.getById(id),
        yandexApi.getStatus(id),
      ]);

      const nextBusiness = businessRes.data.data;
      const nextConnected = statusRes.data.data.connected;
      const nextAdsConnected = statusRes.data.data.adsConnected === true;
      const savedCounterId = nextBusiness.metrikaCounterId?.trim() || '';
      const savedAdsCounterId = window.localStorage.getItem(getAdsCounterStorageKey(id))?.trim() || '';
      const savedAdsGoalId = window.localStorage.getItem(getAdsGoalStorageKey(id))?.trim() || '';
      const resolvedAdsCounterId = savedAdsCounterId || savedCounterId;
      const resolvedAdsAccountType: YandexOAuthAccountType = nextAdsConnected ? 'ADS' : 'PRIMARY';
      const defaultCompetitorQuery = `${nextBusiness.category} ${nextBusiness.city}`.trim();

      setBusiness(nextBusiness);
      setYandexConnected(nextConnected);
      setAdsYandexConnected(nextAdsConnected);
      setCounterId(savedCounterId);
      setAdsCounterId(resolvedAdsCounterId);
      setAdsGoalId(savedAdsGoalId);
      setCompetitorQuery((prev) => prev.trim() || defaultCompetitorQuery);
      await loadCompetitorBusinessStats({ silent: true });
      await loadCompetitorPositions({
        query: defaultCompetitorQuery || nextBusiness.name,
        silent: true,
      });

      if (nextConnected) {
        await loadCounters(id);

        if (savedCounterId) {
          await loadBusinessAnalytics({
            counterId: savedCounterId,
            saveCounter: false,
            silent: true,
          });
        }
      } else {
        setCounters([]);
        setCountersError(null);
        setBusinessAnalytics(null);
        setBusinessAnalyticsError(null);
      }

      if (nextConnected || nextAdsConnected) {
        if (resolvedAdsCounterId) {
          await loadAdsGoals(id, resolvedAdsCounterId, {
            silent: true,
            accountType: resolvedAdsAccountType,
          });
          await loadAdsAnalytics({
            counterId: resolvedAdsCounterId,
            goalId: savedAdsGoalId,
            silent: true,
            accountType: resolvedAdsAccountType,
          });
        } else {
          setAdsGoals([]);
          setAdsGoalsError(null);
          setAdsAnalytics(null);
          setAdsAnalyticsError('Укажите ID счётчика Метрики для рекламной аналитики');
        }
      } else {
        setAdsGoals([]);
        setAdsGoalsError(null);
        setAdsAnalytics(null);
        setAdsAnalyticsError('Подключите Яндекс-аккаунт для рекламной аналитики');
      }
    } catch (e) {
      console.error(e);
      setError('Не удалось загрузить аналитику клиента');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let active = true;

    (async () => {
      try {
        setCompetitorListLoading(true);
        setCompetitorListError(null);
        const response = await competitorApi.getAll(id);
        if (active) {
          setCompetitors(response.data.data);
        }
      } catch (e) {
        console.error(e);
        if (active) {
          setCompetitors([]);
          setCompetitorListError(getApiError(e, 'Не удалось загрузить список конкурентов'));
        }
      } finally {
        if (active) {
          setCompetitorListLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('yandex') !== 'connected') return;
    const tokenType = params.get('tokenType');
    if (tokenType === 'ads') {
      toast.success('Рекламный аккаунт Яндекса успешно подключён');
    } else {
      toast.success('Аккаунт Яндекса успешно подключён');
    }
    params.delete('yandex');
    params.delete('tokenType');
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`;
    window.history.replaceState({}, '', nextUrl);
  }, []);

  useEffect(() => {
    if (!id || (!yandexConnected && !adsYandexConnected)) return;
    const normalizedCounterId = adsCounterId.trim();
    if (!normalizedCounterId) {
      setAdsGoals([]);
      setAdsGoalsError(null);
      return;
    }

    void loadAdsGoals(id, normalizedCounterId, {
      silent: true,
      accountType: adsYandexConnected ? 'ADS' : 'PRIMARY',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, yandexConnected, adsYandexConnected, adsCounterId]);

  const handleConnectDirect = () => {
    if (!id) return;
    setConnecting(true);
    window.location.href = yandexApi.getOAuthStartUrl(id, `/client/${id}/analytics`, 'PRIMARY');
  };

  const handleConnectAds = () => {
    if (!id) return;
    setAdsConnecting(true);
    window.location.href = yandexApi.getOAuthStartUrl(id, `/client/${id}/analytics`, 'ADS');
  };

  const handleDisconnectDirect = async () => {
    if (!id) return;
    try {
      setDisconnecting(true);
      await yandexApi.disconnect(id, 'PRIMARY');
      setYandexConnected(false);
      setCounters([]);
      setCountersError(null);
      setBusinessAnalytics(null);
      setBusinessAnalyticsError(null);
      if (!adsYandexConnected) {
        setAdsGoals([]);
        setAdsGoalsError(null);
        setAdsAnalytics(null);
        setAdsAnalyticsError('Подключите Яндекс-аккаунт для рекламной аналитики');
      }
      toast.success('Аккаунт Яндекса отключён');
    } catch (e) {
      console.error(e);
      toast.error('Не удалось отключить аккаунт');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleDisconnectAds = async () => {
    if (!id) return;
    try {
      setAdsDisconnecting(true);
      await yandexApi.disconnect(id, 'ADS');
      setAdsYandexConnected(false);
      setAdsGoals([]);
      setAdsGoalsError(null);
      setAdsAnalytics(null);
      setAdsAnalyticsError('Рекламный аккаунт отключён. Подключите другой аккаунт или используйте основной.');
      setAdsCounterId('');
      setAdsGoalId('');
      window.localStorage.removeItem(getAdsCounterStorageKey(id));
      window.localStorage.removeItem(getAdsGoalStorageKey(id));
      toast.success('Рекламный аккаунт Яндекса отключён');
    } catch (e) {
      console.error(e);
      toast.error('Не удалось отключить рекламный аккаунт');
    } finally {
      setAdsDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="p-6">
        <Card>
          <p className="text-sm text-red-600">{error || 'Клиент не найден'}</p>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={`Аналитика: ${business.name}`}
        subtitle="Три блока аналитики по карточке, конкурентам и рекламе"
      />

      <div className="p-6 space-y-6">
        <Button
          variant="link"
          onClick={() => navigate(`/client/${business.id}/dashboard`)}
          icon={<ArrowLeft className="w-4 h-4" />}
          className="text-gray-500 hover:text-gray-700 hover:no-underline"
        >
          Вернуться в дашборд клиента
        </Button>

        <div className="grid grid-cols-1 gap-6">
          <Card>
            <div className="space-y-5">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <Building2 className="w-5 h-5" />
                </span>
                <h3 className="text-base font-semibold text-gray-900">
                  1. Аналитика с карточки Яндекс Бизнес
                </h3>
              </div>

              <p className="text-sm text-gray-600">
                Подтягивание метрик карточки через подключённый аккаунт Яндекса и счётчик Метрики.
              </p>

              <div
                className={clsx(
                  'flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium',
                  yandexConnected
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'border-gray-200 bg-gray-50 text-gray-600',
                )}
              >
                {yandexConnected ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Аккаунт Яндекса подключён
                  </>
                ) : (
                  <>
                    <Unplug className="w-4 h-4" />
                    Аккаунт Яндекса не подключён
                  </>
                )}
              </div>

              {!yandexConnected ? (
                <Button
                  variant="primary"
                  onClick={handleConnectDirect}
                  loading={connecting}
                >
                  {connecting ? 'Подключение...' : 'Подключить Яндекс'}
                </Button>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Дата от</label>
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Дата до</label>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">ID счётчика Метрики</label>
                      <input
                        type="text"
                        value={counterId}
                        onChange={(e) => setCounterId(e.target.value)}
                        placeholder="Например: 12345678"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>

                  {loadingCounters ? (
                    <div className="inline-flex items-center gap-2 text-xs text-gray-500">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Загружаем доступные счётчики...
                    </div>
                  ) : counters.length > 0 ? (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Доступные счётчики (из аккаунта)
                      </label>
                      <select
                        value={counterId}
                        onChange={(e) => setCounterId(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">Выберите счётчик</option>
                        {counters.map((counter) => (
                          <option key={counter.id} value={counter.id}>
                            {counter.name} ({counter.id})
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}

                  {countersError && (
                    <p className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700">
                      {countersError}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      onClick={handleConnectDirect}
                      loading={connecting}
                      className="border-primary-300 text-primary-700 hover:bg-primary-50"
                    >
                      {connecting ? 'Переподключение...' : 'Переподключить Яндекс'}
                    </Button>

                    <Button
                      variant="primary"
                      onClick={() => loadBusinessAnalytics({ saveCounter: true })}
                      loading={loadingBusinessAnalytics}
                      icon={<RefreshCw className="w-4 h-4" />}
                    >
                      {loadingBusinessAnalytics ? 'Загружаем...' : 'Подтянуть аналитику'}
                    </Button>

                    <Button
                      variant="danger"
                      onClick={handleDisconnectDirect}
                      loading={disconnecting}
                    >
                      {disconnecting ? 'Отключение...' : 'Отключить аккаунт'}
                    </Button>
                  </div>
                </>
              )}

              {businessAnalyticsError && (
                <p className="text-xs text-red-600">{businessAnalyticsError}</p>
              )}

              {businessAnalytics && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard title="Просмотры" value={formatMetric(businessAnalytics.views)} icon={Eye} iconColor="primary" />
                  <StatCard title="Переходы на сайт" value={formatMetric(businessAnalytics.websiteClicks)} icon={MousePointerClick} iconColor="blue" />
                  <StatCard title="Звонки" value={formatMetric(businessAnalytics.phoneCalls)} icon={Phone} iconColor="green" />
                  <StatCard title="Маршруты" value={formatMetric(businessAnalytics.routes)} icon={Route} iconColor="purple" />
                  <StatCard title="Трафик: Поиск" value={formatMetric(businessAnalytics.trafficSearch)} icon={BarChart3} iconColor="primary" />
                  <StatCard title="Трафик: Карты" value={formatMetric(businessAnalytics.trafficMaps)} icon={BarChart3} iconColor="blue" />
                  <StatCard title="Трафик: Навигатор" value={formatMetric(businessAnalytics.trafficNavigator)} icon={BarChart3} iconColor="orange" />
                  <StatCard title="Трафик: Прямой" value={formatMetric(businessAnalytics.trafficDirect)} icon={BarChart3} iconColor="green" />
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div className="space-y-5">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <Radar className="w-5 h-5" />
                </span>
                <h3 className="text-base font-semibold text-gray-900">
                  2. Аналитика конкурентов с карточки Яндекс Бизнес
                </h3>
              </div>
              <p className="text-sm text-gray-600">
                Статистика конкурентов за крайние 7 дней из Яндекс Бизнес и позиции в выдаче Яндекс.Карт.
              </p>

              <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50/60 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Статистика из вкладки «Конкуренты» (7 дней)
                    </p>
                    <p className="text-xs text-gray-500">
                      Источник: Yandex Business (owner-session)
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    onClick={() => loadCompetitorBusinessStats()}
                    loading={loadingCompetitorBusinessStats}
                    icon={<RefreshCw className="w-4 h-4" />}
                  >
                    {loadingCompetitorBusinessStats ? 'Загружаем...' : 'Подтянуть 7 дней'}
                  </Button>
                </div>

                {competitorBusinessStatsError && (
                  <p className="text-xs text-red-600">{competitorBusinessStatsError}</p>
                )}

                {competitorBusinessStats && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">
                      Обновлено: {new Date(competitorBusinessStats.checkedAt).toLocaleString('ru-RU')}
                    </p>
                    {competitorBusinessStats.note && (
                      <p className="text-xs text-amber-700">{competitorBusinessStats.note}</p>
                    )}

                    {competitorBusinessStats.rows.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>#</TableHead>
                            <TableHead>Конкурент</TableHead>
                            <TableHead className="text-right">Просмотры</TableHead>
                            <TableHead className="text-right">Звонки</TableHead>
                            <TableHead className="text-right">Маршруты</TableHead>
                            <TableHead className="text-right">Переходы</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {competitorBusinessStats.rows.map((row) => (
                            <TableRow key={`${row.position}-${row.name}`}>
                              <TableCell>{row.position}</TableCell>
                              <TableCell className="font-medium text-gray-900">{row.name}</TableCell>
                              <TableCell className="text-right">{formatMetric(row.views ?? undefined)}</TableCell>
                              <TableCell className="text-right">{formatMetric(row.phoneCalls ?? undefined)}</TableCell>
                              <TableCell className="text-right">{formatMetric(row.routes ?? undefined)}</TableCell>
                              <TableCell className="text-right">{formatMetric(row.websiteClicks ?? undefined)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-xs text-gray-600">
                        Яндекс Бизнес не вернул строки конкурентов для этой карточки.
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
                <input
                  type="text"
                  value={competitorQuery}
                  onChange={(e) => setCompetitorQuery(e.target.value)}
                  placeholder="Например: стоматология Москва"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <Button
                  variant="primary"
                  onClick={() => loadCompetitorPositions({ query: competitorQuery })}
                  loading={loadingCompetitors}
                  icon={<RefreshCw className="w-4 h-4" />}
                >
                  {loadingCompetitors ? 'Проверяем...' : 'Обновить позиции'}
                </Button>
              </div>

              {competitorError && (
                <p className="text-xs text-red-600">{competitorError}</p>
              )}

              <div className="space-y-3 rounded-xl border border-gray-200 p-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Отслеживаемые конкуренты</p>
                  <p className="mt-1 text-xs text-gray-500">
                    Добавьте конкурентов, чтобы видеть их позиции в выдаче Яндекс.Карт.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_auto]">
                  <Input
                    type="text"
                    value={newCompetitorName}
                    onChange={(e) => setNewCompetitorName(e.target.value)}
                    placeholder="Название конкурента"
                  />
                  <Input
                    type="text"
                    value={newCompetitorUrl}
                    onChange={(e) => setNewCompetitorUrl(e.target.value)}
                    placeholder="Ссылка на Яндекс.Карты"
                  />
                  <Button
                    variant="primary"
                    onClick={handleAddCompetitor}
                    loading={competitorSubmitting}
                  >
                    Добавить
                  </Button>
                </div>

                {competitorListError && (
                  <p className="text-xs text-red-600">{competitorListError}</p>
                )}

                {competitorListLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Загружаем конкурентов...
                  </div>
                ) : competitors.length > 0 ? (
                  <div className="overflow-hidden rounded-xl border border-gray-200">
                    <div className="divide-y divide-gray-100">
                      {competitors.map((competitor) => (
                        <div
                          key={competitor.id}
                          className="grid grid-cols-[1fr_auto] items-center gap-3 px-4 py-2.5 text-sm"
                        >
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900">{competitor.name}</p>
                            <p className="truncate text-xs text-gray-500">{competitor.yandexMapsUrl}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveCompetitor(competitor.id)}
                            icon={<Trash2 className="h-4 w-4" />}
                          >
                            Удалить
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">Конкуренты не добавлены</p>
                )}
              </div>

              {competitorData && (
                <div className="space-y-3">
                  <div className="rounded-xl border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-gray-700">
                    <p>
                      Запрос: <span className="font-medium text-gray-900">{competitorData.query}</span>
                    </p>
                    <p className="mt-1">
                      Позиция вашей карточки:{' '}
                      <span className="font-semibold text-gray-900">
                        {competitorData.ownPosition ? `#${competitorData.ownPosition}` : 'не в топ-10'}
                      </span>
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Обновлено: {new Date(competitorData.checkedAt).toLocaleString('ru-RU')}
                    </p>
                    {competitorData.note && (
                      <p className="mt-2 text-xs text-amber-700">{competitorData.note}</p>
                    )}
                  </div>

                  {competitorData.trackedCompetitors.length > 0 && (
                    <div className="overflow-hidden rounded-xl border border-gray-200">
                      <div className="bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Добавленные конкуренты
                      </div>
                      <div className="divide-y divide-gray-100">
                        {competitorData.trackedCompetitors.map((competitor) => (
                          <div key={competitor.competitorId} className="grid grid-cols-[1fr_auto] items-center gap-3 px-4 py-2.5 text-sm">
                            <div>
                              <p className="font-medium text-gray-900">{competitor.name}</p>
                              <p className="text-xs text-gray-500">{competitor.orgId || 'orgId не указан'}</p>
                            </div>
                            <div className="text-right">
                              {competitor.position ? (
                                <Badge tone="success">#{competitor.position}</Badge>
                              ) : (
                                <Badge tone="neutral">вне топ-10</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="overflow-hidden rounded-xl border border-gray-200">
                    <div className="bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Топ-10 выдачи Яндекс.Карт
                    </div>
                    {competitorData.topResults.length > 0 ? (
                      <div className="divide-y divide-gray-100">
                        {competitorData.topResults.map((item) => (
                          <div key={item.orgId} className="grid grid-cols-[auto_1fr] items-start gap-3 px-4 py-2.5 text-sm">
                            <span className="font-semibold text-gray-700">#{item.position}</span>
                            <div>
                              <p className="font-medium text-gray-900">{item.name}</p>
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noreferrer"
                                className="break-all text-xs text-primary-600 underline hover:text-primary-700"
                              >
                                {item.orgId}
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500">
                        По этому запросу сейчас нет доступных результатов в топе.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div className="space-y-5">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <Target className="w-5 h-5" />
                </span>
                <h3 className="text-base font-semibold text-gray-900">
                  3. Аналитика с рекламы в Яндекс Директ
                </h3>
              </div>
              <p className="text-sm text-gray-600">
                Показы, клики, CTR, расходы, эффективность кампаний и динамика рекламных метрик.
              </p>

              <div
                className={clsx(
                  'rounded-xl border px-3 py-2.5 text-sm font-medium',
                  adsYandexConnected
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'border-gray-200 bg-gray-50 text-gray-600',
                )}
              >
                <div className="flex items-center gap-2">
                  {adsYandexConnected ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Подключён отдельный рекламный аккаунт
                    </>
                  ) : (
                    <>
                      <Unplug className="w-4 h-4" />
                      Отдельный рекламный аккаунт не подключён
                    </>
                  )}
                </div>
                {!adsYandexConnected && yandexConnected && (
                  <p className="mt-1 text-xs font-normal text-gray-600">
                    Пока используется основной аккаунт Яндекса. Можно подключить отдельный рекламный аккаунт.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2">
                <input
                  type="text"
                  value={adsCounterId}
                  onChange={(e) => setAdsCounterId(e.target.value)}
                  placeholder="ID счётчика Метрики для рекламы (если отличается)"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!id) return;
                    void loadAdsGoals(id, adsCounterId, {
                      accountType: adsYandexConnected ? 'ADS' : 'PRIMARY',
                    });
                  }}
                  disabled={!adsCounterId.trim()}
                  loading={loadingAdsGoals}
                >
                  {loadingAdsGoals ? 'Цели...' : 'Подтянуть цели'}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => loadAdsAnalytics({
                    accountType: adsYandexConnected ? 'ADS' : 'PRIMARY',
                  })}
                  loading={loadingAdsAnalytics}
                  icon={<RefreshCw className="w-4 h-4" />}
                >
                  {loadingAdsAnalytics ? 'Загружаем...' : 'Подтянуть из Метрики'}
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  type="text"
                  value={adsGoalId}
                  onChange={(e) => setAdsGoalId(e.target.value)}
                  placeholder="ID цели для конверсий (необязательно)"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <select
                  value={adsGoalId}
                  onChange={(e) => setAdsGoalId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Без цели (конверсии = звонки + маршруты + переходы)</option>
                  {adsGoals.map((goal) => (
                    <option key={goal.id} value={goal.id}>
                      {goal.name} ({goal.id})
                    </option>
                  ))}
                </select>
              </div>

              {adsGoalsError && (
                <p className="text-xs text-amber-700">{adsGoalsError}</p>
              )}

              {loadingAdsAnalytics ? (
                <div className="inline-flex items-center gap-2 text-xs text-gray-500">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Загружаем рекламную аналитику...
                </div>
              ) : adsAnalytics ? (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500">
                    Источник: Метрика, счётчик {adsAnalytics.counterId}, период {new Date(`${adsAnalytics.dateFrom}T00:00:00`).toLocaleDateString('ru-RU')} - {new Date(`${adsAnalytics.dateTo}T00:00:00`).toLocaleDateString('ru-RU')}
                  </p>
                  <p className="text-xs text-gray-500">
                    Цель: {adsAnalytics.goalId
                      ? (adsAnalytics.goalName
                        ? `${adsAnalytics.goalName} (${adsAnalytics.goalId})`
                        : adsAnalytics.goalId)
                      : 'не выбрана (используется сумма действий карточки)'}
                  </p>

                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    <StatCard title="Показы" value={formatMetric(adsAnalytics.impressions)} icon={Eye} iconColor="primary" />
                    <StatCard title="Клики" value={formatMetric(adsAnalytics.clicks)} icon={MousePointerClick} iconColor="blue" />
                    <StatCard
                      title="CTR"
                      value={`${adsAnalytics.ctr.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}%`}
                      icon={BarChart3}
                      iconColor="green"
                    />
                    <StatCard title="Расход" value={formatDecimal(adsAnalytics.spend)} icon={Target} iconColor="orange" />
                    <StatCard title="Конверсии" value={formatMetric(adsAnalytics.conversions)} icon={Target} iconColor="green" />
                    <StatCard
                      title="Стоимость конверсии"
                      value={adsAnalytics.conversions > 0 ? formatDecimal(adsAnalytics.costPerConversion) : '—'}
                      icon={Target}
                      iconColor="purple"
                    />
                    <StatCard title="Прямой трафик" value={formatMetric(adsAnalytics.directTraffic)} icon={Route} iconColor="blue" />
                  </div>

                  {adsAnalytics.spend === 0 && (
                    <p className="text-xs text-amber-700">
                      Расход равен 0. Проверьте, что в Метрике подключены расходы из Яндекс.Директа.
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-500">
                  Нажмите «Подтянуть из Метрики», чтобы загрузить рекламные метрики за выбранный период.
                </p>
              )}

              {adsAnalyticsError && (
                <p className="text-xs text-red-600">{adsAnalyticsError}</p>
              )}

              <div className="flex flex-wrap gap-2">
                {!adsYandexConnected ? (
                  <Button
                    variant="primary"
                    onClick={handleConnectAds}
                    loading={adsConnecting}
                  >
                    {adsConnecting ? 'Подключение...' : 'Подключить рекламный аккаунт'}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={handleConnectAds}
                      loading={adsConnecting}
                      className="border-primary-300 text-primary-700 hover:bg-primary-50"
                    >
                      {adsConnecting ? 'Переподключение...' : 'Переподключить Яндекс'}
                    </Button>

                    <Button
                      variant="danger"
                      onClick={handleDisconnectAds}
                      loading={adsDisconnecting}
                    >
                      {adsDisconnecting ? 'Отключение...' : 'Отключить аккаунт'}
                    </Button>
                  </>
                )}

              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
